/**
 * Bounded reason codes for desktop update failures.
 *
 * electron-updater error strings embed the absolute staging path, which on
 * Windows contains the OS username
 * (`C:\Users\<name>\AppData\Local\<app>-updater\pending\…`). Sending them to
 * PostHog would put PII in an analytics property and give it unbounded
 * cardinality, so only a code from this set is ever tracked. Sentry keeps the
 * full error for debugging.
 */
export enum UpdateErrorReason {
  /** Staged copy corrupted or locked mid-apply — usually endpoint AV/EDR. */
  ApplyBlocked = 'apply_blocked',
  /** Staged installer failed its code-signature check. */
  SignatureRejected = 'signature_rejected',
  /** Download never reached the bucket, or the socket dropped. */
  Network = 'network',
  /** The channel manifest or artifact is missing (often a half-published release). */
  NotFound = 'not_found',
  /** The update server returned 5xx. */
  ServerError = 'server_error',
  /** Downloaded bytes did not match the manifest hash. */
  ChecksumMismatch = 'checksum_mismatch',
  /** The updater could not write where it needed to. */
  PermissionDenied = 'permission_denied',
  /** No room to stage the download. */
  DiskFull = 'disk_full',
  Unknown = 'unknown',
}

// Ordered: the first match wins, so the specific staging-dir cases are tested
// before the generic filesystem codes they share.
const REASON_PATTERNS: Array<[UpdateErrorReason, RegExp]> = [
  [UpdateErrorReason.DiskFull, /enospc|not enough space|disk full/],
  [
    UpdateErrorReason.SignatureRejected,
    /is not signed at all|code object is not signed|invalid resource directory|signature/,
  ],
  [
    UpdateErrorReason.ApplyBlocked,
    /ditto:|\.shipit|\\pending\\|ebusy|locked by another process/,
  ],
  [UpdateErrorReason.ChecksumMismatch, /sha512|checksum|integrity/],
  [
    UpdateErrorReason.PermissionDenied,
    /eacces|eperm|access is denied|permission denied/,
  ],
  [UpdateErrorReason.NotFound, /\b404\b|not found/],
  [
    // Status codes are word-bounded so a byte offset or duration in the
    // message ("position 5000", "500ms") is not read as an HTTP 5xx.
    UpdateErrorReason.ServerError,
    /\b50\d\b|internal server error|bad gateway|service unavailable/,
  ],
  [
    // ENOTFOUND and EAI_AGAIN are both DNS resolution failures, not a missing
    // release — they belong here, with the rest of the connectivity errors.
    UpdateErrorReason.Network,
    /econnreset|etimedout|enotfound|eai_again|net::err_|socket hang up|network|timed? ?out/,
  ],
]

/**
 * Map an updater error onto a reason code. Never returns any part of the input.
 */
export const classifyUpdateError = (
  message?: string | null,
): UpdateErrorReason => {
  if (!message) {
    return UpdateErrorReason.Unknown
  }
  const haystack = message.toLowerCase()
  const match = REASON_PATTERNS.find(([, pattern]) => pattern.test(haystack))
  return match ? match[0] : UpdateErrorReason.Unknown
}
